import { useEffect, useState, useCallback } from 'react';
import { getMasqueradingStatus, stopMasquerading as apiStopMasquerading, type MasqueradingStatus } from '@/utils/api-client';
import { sessionAuthService } from '@/services/session-auth-service';

interface UseMasqueradeReturn {
  isMasquerading: boolean;
  masqueradedUserEmail: string | null;
  originalAdminId: string | null;
  isLoading: boolean;
  stopMasquerading: () => void;
}

export function useMasquerade(): UseMasqueradeReturn {
  const [status, setStatus] = useState<MasqueradingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkMasqueradingStatus() {
      try {
        const result = await getMasqueradingStatus();
        setStatus(result);
      } catch {
        setStatus({ is_masquerading: false });
      } finally {
        setIsLoading(false);
      }
    }

    checkMasqueradingStatus();
  }, []);

  const stopMasquerading = useCallback(async () => {
    try {
      // Call API to stop masquerading - this now restores the original admin session
      const response = await apiStopMasquerading();

      // If admin session was restored, update session storage
      if (response.admin) {
        sessionAuthService.setAdminInfo({
          id: response.admin.id,
          type: response.admin.type,
          level: response.admin.level,
          email: response.admin.email,
          name: response.admin.name,
          permissions: response.admin.permissions,
        });
      }

      // Redirect to users page in admin
      window.location.href = response.redirect_url || '/users';
    } catch (err) {
      console.warn('Stop masquerading API call failed:', err);
      // Still redirect even if API fails
      window.location.href = '/users';
    }
  }, []);

  // Get the email from session storage (the current masqueraded user's email)
  const adminInfo = sessionAuthService.getAdminInfo();
  const masqueradedUserEmail = status?.is_masquerading ? (adminInfo?.email ?? null) : null;

  return {
    isMasquerading: status?.is_masquerading ?? false,
    masqueradedUserEmail,
    originalAdminId: status?.original_admin_user_id ?? null,
    isLoading,
    stopMasquerading,
  };
}
